import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { categories } from "@/mocks/navCategories";
import { navSites } from "@/mocks/navSites";

const values = [
  {
    icon: "ri-eye-line",
    title: "信息一目了然",
    desc: "每个网站只保留必要信息：名称、域名、用途与适用人群，避免信息噪音。",
  },
  {
    icon: "ri-hand-heart-line",
    title: "人工筛选",
    desc: "由编辑逐条核查站点用途与可访问性，拒绝纯广告与低质聚合页。",
  },
  {
    icon: "ri-smartphone-line",
    title: "桌面手机一致",
    desc: "响应式布局，手机端单列卡片与加载更多，通勤途中同样顺手。",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "尊重隐私",
    desc: "不要求注册即可使用，仅在浏览器本地保存收藏，不做跨站追踪。",
  },
];

const steps = [
  { step: "01", title: "选择分类", desc: "从一级分类进入，或用顶部搜索框直接找网站。" },
  { step: "02", title: "浏览对比", desc: "查看简介、标签与适用人群，快速判断是否合适。" },
  { step: "03", title: "一键访问", desc: "在详情页点击访问按钮，新标签页打开目标网站。" },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "关于我们 - 51网址导航";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "了解51网址导航的定位、网址筛选标准、使用说明与隐私声明，以及提交网址与反馈的联系方式。",
    );
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="relative w-full bg-primary-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20deep%20navy%20blue%20gradient%20background%20with%20soft%20thin%20lines%20and%20subtle%20orange%20light%20glow%20in%20the%20corner%2C%20minimal%20calm%20dark%20atmosphere%20with%20strong%20contrast%20for%20white%20text%2C%20modern%20editorial%20technology%20visual&width=1920&height=700&seq=51nav-about-bg-01&orientation=landscape"
              alt="51网址导航关于页背景，深蓝渐变抽象视觉"
              title="关于51网址导航"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/80 to-primary-900/60" />
          </div>
          <div className="relative w-full px-4 md:px-6 py-12 md:py-16">
            <nav className="text-xs text-background-100/70 flex items-center gap-1">
              <Link to="/" className="hover:text-background-50 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">关于我们</span>
            </nav>
            <h1 className="mt-4 font-heading font-extrabold text-2xl md:text-4xl text-background-50 tracking-tight">
              把常用的网站，整理成一个干净的入口
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-background-100/85 leading-7">
              51网址导航始于一个很朴素的需求：每天打开浏览器，总是反复输入那几个域名。
              我们把这些高频站点按生活、购物、娱乐、学习等领域整理好，让访问本身不再耗时间。
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/"
                className="px-4 py-2 rounded-md bg-background-50 text-primary-700 text-xs font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                开始浏览
              </Link>
              <a
                href="#feedback"
                className="px-4 py-2 rounded-md bg-accent-500 text-background-50 text-xs font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                提交网址
              </a>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { value: navSites.length, label: "收录网站", suffix: "个" },
              { value: categories.length, label: "一级分类", suffix: "类" },
              { value: 32, label: "二级领域", suffix: "个" },
              { value: 5, label: "人工复核", suffix: "次/周" },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-lg bg-background-100 border border-background-200 px-4 py-4"
              >
                <p className="font-heading font-extrabold text-2xl md:text-3xl text-primary-600">
                  {item.value}
                  <span className="text-xs font-normal text-foreground-400 ml-1">{item.suffix}</span>
                </p>
                <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 md:gap-6 mt-6 md:mt-8">
            <div className="min-w-0 space-y-4 md:space-y-6">
              <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
                <h2 className="font-heading font-bold text-lg text-foreground-950">我们在做什么</h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {values.map((item) => (
                    <div key={item.title} className="p-4 rounded-lg bg-background-100 border border-background-200">
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50 text-primary-600">
                        <i className={`${item.icon} text-base`} />
                      </span>
                      <h3 className="mt-2.5 text-sm font-medium text-foreground-950">{item.title}</h3>
                      <p className="mt-1 text-xs text-foreground-500 leading-5">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
                <h2 className="font-heading font-bold text-lg text-foreground-950">三步使用说明</h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {steps.map((item) => (
                    <div key={item.step} className="p-4 rounded-lg border border-background-200">
                      <span className="font-heading font-extrabold text-xl text-accent-500">{item.step}</span>
                      <h3 className="mt-1.5 text-sm font-medium text-foreground-950">{item.title}</h3>
                      <p className="mt-1 text-xs text-foreground-500 leading-5">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-md bg-secondary-50 border border-secondary-100">
                  <p className="text-xs text-secondary-800 leading-6">
                    <span className="font-medium">关于收藏功能：</span>
                    目前没有账号体系，收藏保存在浏览器本地（localStorage），仅用于功能演示。
                    清除浏览器数据、更换设备或使用无痕模式后收藏不会保留，也不支持云端同步。
                  </p>
                </div>
              </section>

              <section
                id="privacy"
                className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6"
              >
                <h2 className="font-heading font-bold text-lg text-foreground-950">隐私声明</h2>
                <ul className="mt-3 space-y-2 text-xs md:text-sm text-foreground-600 leading-6">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-1" />
                    浏览本站无需注册登录，我们不会主动收集你的个人信息。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-1" />
                    收藏、浏览偏好等数据仅保存在你当前浏览器本地，不会上传到服务器。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-1" />
                    通过反馈表单主动提交的邮箱与内容，仅用于网址审核与回复，不会对外公开。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-1" />
                    本站链接指向第三方网站，其内容与隐私政策由对应站点负责。
                  </li>
                </ul>
              </section>

              <section
                id="contact"
                className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6"
              >
                <h2 className="font-heading font-bold text-lg text-foreground-950">联系方式</h2>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href="mailto:hello@51nav.example.com"
                    className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:text-primary-600 cursor-pointer"
                  >
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-mail-line" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-foreground-400">合作与反馈邮箱</span>
                      <span className="block text-sm text-foreground-900 truncate">hello@51nav.example.com</span>
                    </span>
                  </a>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-background-100">
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-time-line" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-foreground-400">响应时间</span>
                      <span className="block text-sm text-foreground-900">工作日 10:00 - 18:00</span>
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-foreground-400">
                  当前为演示站点，邮箱与响应时间为示例信息，实际以运营方配置为准。
                </p>
              </section>
            </div>

            <div className="space-y-4">
              <FeedbackForm />

              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950">按分类快速回去</h2>
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-background-50 text-xs text-foreground-700 hover:text-primary-600 cursor-pointer"
                    >
                      <i className={`${cat.icon} text-sm text-primary-500`} />
                      <span className="truncate">{cat.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}